import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { workspaceApi } from '@/lib/api'
import { queryClient } from '@/lib/query-client'
import type { WorkspaceVisibility } from '@/types/workspace'

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export default function NewWorkspacePage() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<WorkspaceVisibility>('private')
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const [error, setError] = useState<string | null>(null)

  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManuallyEdited) {
      setSlug(toSlug(name))
    }
  }, [name, slugManuallyEdited])

  // Check slug availability
  useEffect(() => {
    if (slug.length < 3) {
      setSlugStatus('idle')
      return
    }
    setSlugStatus('checking')
    const timer = setTimeout(async () => {
      try {
        const result = await workspaceApi.checkSlug(slug)
        setSlugStatus(result.available ? 'available' : 'taken')
      } catch {
        setSlugStatus('idle')
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [slug])

  const createMutation = useMutation({
    mutationFn: workspaceApi.createWorkspace,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      navigate(`/workspaces/${data.slug}`, { replace: true })
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Gagal membuat workspace.')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name || !slug) return
    if (slugStatus === 'taken') {
      setError('Slug sudah digunakan. Pilih slug lain.')
      return
    }
    createMutation.mutate({ name, slug, description: description || undefined, visibility })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Buat Workspace</h1>
        <p className="text-sm text-muted-foreground">Isi detail workspace baru kamu.</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-lg space-y-5 rounded-xl border border-border bg-card p-6">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="text-sm font-medium">
            Nama <span className="text-destructive">*</span>
          </label>
          <input
            id="name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nama workspace"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Slug */}
        <div className="space-y-1.5">
          <label htmlFor="slug" className="text-sm font-medium">
            Slug <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <input
              id="slug"
              type="text"
              required
              value={slug}
              onChange={(e) => {
                setSlugManuallyEdited(true)
                setSlug(toSlug(e.target.value))
              }}
              placeholder="nama-workspace"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {slug.length >= 3 && (
            <p className={`text-xs ${slugStatus === 'available' ? 'text-green-600 dark:text-green-400' : slugStatus === 'taken' ? 'text-destructive' : 'text-muted-foreground'}`}>
              {slugStatus === 'checking' && 'Memeriksa ketersediaan...'}
              {slugStatus === 'available' && `/${slug} tersedia`}
              {slugStatus === 'taken' && `/${slug} sudah digunakan`}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label htmlFor="description" className="text-sm font-medium">
            Deskripsi
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Deskripsi singkat workspace (opsional)"
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        {/* Visibility */}
        <div className="space-y-1.5">
          <label htmlFor="visibility" className="text-sm font-medium">
            Visibilitas
          </label>
          <select
            id="visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as WorkspaceVisibility)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="private">Privat</option>
            <option value="invite_only">Invite Only</option>
            <option value="public">Publik</option>
          </select>
        </div>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={createMutation.isPending || slugStatus === 'taken'}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {createMutation.isPending ? 'Membuat...' : 'Buat Workspace'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/workspaces')}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  )
}
